import { Col, Form, Label, Row } from "reactstrap";
import { Formik } from "formik";

//Common
import { FormBackGround } from "../../common/Forms";
import {
    Checkbox,
    ConfirmationDialog,
} from "../../common/VFX-UI";
import {
    PledgeActions,
    PledgeHeader,
    PrivacySection,
    SpouseSection,
} from "./Form/EmployeePledgeFormSections";
import ContributionSection from "./Form/ContributionSection";
import GiftInvestmentSection from "./Form/GiftInvestmentSection";
import PaymentMethodSection from "./Form/PaymentMethodSection";
import { useEmployeePledgeForm } from "./Form/hooks/useEmployeePledgeForm";

//App
import { ContributionOption, EmployeePledgeFormProps } from "../../../types";
import {
    UNDESIGNATED_OPTION_ID,
    privacyOptions,
} from "./constants";
import employeePledgeValidationSchema from "./validationSchema";

import { getFormattedCurrency } from "../../../utility/helpers";
import { IsMobile } from "../../../utility/lib/breakpoints";
import { EmployeePledgeViewModel } from "../../../viewModels";
import { EmployeePledgeGiftInvestmentViewModel } from "../../../viewModels/employeePledgeGiftInvestmentViewModel";

const EmployeePledgeForm = (props: EmployeePledgeFormProps) => {
    const isMobile = IsMobile();
    const {
        width,
        isEnableReinitialize,
        formRef,
        isEditModalOpen,
        setIsEditModalOpen,
        enableOtherAnnualPledgeAmount,
        showSpouseOtherOptions,
        setShowSpouseOtherOptions,
        minimumContributionMessage,
        setMinimumContributionMessage,
        contributionOptions,
        sortedAgency,
        paymentOptions,
        pledgeStyles,
        undesignatedLabel,
        filteredGiftInvestmentOptions,
        setOtherGiftOption,
        displayBottom,
        handleGiftInvestmentChange,
        checkMinimumContribution,
        handleContributionOptionChange,
        recalculateRemainder,
        saveInput,
        openEditDialog,
        closeEditDialog,
        isDesktop,
        stepUpEnrollmentEnabled,
        leadershipEnrollmentEnabled,
        stepUpMinDonationAmount,
        leadershipMinDonationAmount,
    } = useEmployeePledgeForm(props);
    const initialValues = Object.assign(
        new EmployeePledgeViewModel(),
        props.initialVal,
        {
            // Keep the response selected in the form while the confirmation
            // dialog is open. The parent records the submitted values to build
            // the confirmation summary, which can reinitialize Formik.
            isNotWillingToContribute: props.initialVal.isNotWillingToContribute,
            isWillingToStayAnonymous: null,
            isWillingToCombineSpouseContribution: null,
            contributionOptionId: props.pledgeQuery?.hasResponded
                ? props.initialVal.contributionOptionId
                : ContributionOption.Other,
            leadershipMinDonationAmount,
            stepUpMinDonationAmount,
        }
    );
    return (
        <>
            <FormBackGround>
                <Formik
                    initialValues={initialValues}
                    validationSchema={employeePledgeValidationSchema}
                    enableReinitialize={isEnableReinitialize}
                    onSubmit={(values) => {
                        if (values.isWillingToCombineSpouseContribution !== privacyOptions[1].value) {
                            values.spouseName = null;
                            values.spouseWorkPlace = null;
                            values.spousePledgeAmount = null;
                        }
                        props.handleOnSubmit(
                            Object.assign(new EmployeePledgeViewModel(), values)
                        );
                    }}
                    innerRef={formRef}
                >
                    {({ values, setFieldValue, isValid, dirty, setFieldTouched }) => {

                        const unEditActionHandler = () => {
                            const equalsContribution = values.giftInvestmentAmount?.find(
                                (x: EmployeePledgeGiftInvestmentViewModel) =>
                                    x.giftInvestmentId === UNDESIGNATED_OPTION_ID
                            );
                            if (equalsContribution === undefined) setIsEditModalOpen(false);
                            else if ((equalsContribution.pledgeAmount ?? 0) < 0) {
                                setIsEditModalOpen(true);
                            } else setIsEditModalOpen(false);
                        };
                        const canDisplayMethodOfGiving =
                            !!values.contributionOptionId && !values.isNotWillingToContribute;
                        const canDisplayPrivacy =
                            !values.isNotWillingToContribute &&
                            (values.giftInvestment?.length ?? 0) > 0;
                        const canDisplayCombineContribution =
                            !values.isNotWillingToContribute &&
                            (values.giftInvestment?.length ?? 0) > 0;
                        const dropDownArrayLength = values?.giftInvestmentAmount?.filter(
                            (x: EmployeePledgeGiftInvestmentViewModel) => x.isDropdown
                        ).length;
                        return (
                            <div
                                className="p-3 p-md-4 mx-auto"
                                style={{ minHeight: 750, maxWidth: 1280, ...pledgeStyles.pageContainer }}
                            >
                                {!isMobile && (
                                    <PledgeHeader
                                        employee={props.employee}
                                        campaign={props.campaign}
                                        styles={pledgeStyles}
                                    />
                                )}
                                <Row className="mb-4 p-3 align-items-center" style={{ backgroundColor: "#EDF7EC", borderRadius: 6 }}>
                                    <Col xs={12} md={5} className="mb-2 mb-md-0">
                                        <Label style={{ ...pledgeStyles.sectionHeader, fontSize: "1.125rem", marginTop: 0 }}>
                                            The Gottlieb Society Of Capital Area United Way
                                        </Label>
                                        <br />
                                        <a
                                            href="https://www.cauw.org/leadership-giving-societies"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Learn more about leadership giving
                                        </a>
                                    </Col>
                                    <Col xs={12} md={7}>
                                        {stepUpEnrollmentEnabled && (
                                            <div>
                                                <Checkbox
                                                    label={`I would like to enroll in the Step Up program. ${stepUpMinDonationAmount
                                                            ? `Minimum Donation is 
                              ${getFormattedCurrency(stepUpMinDonationAmount)}.`
                                                            : ""
                                                        }`}
                                                    name="stepUpEnrollment"
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setFieldValue("leadershipEnrollment", false);
                                                            values?.contributionOptionId &&
                                                                checkMinimumContribution(
                                                                    values.contributionAmount ?? 0,
                                                                    { stepUpEnrollment: true }
                                                                );
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}
                                        {leadershipEnrollmentEnabled && (
                                            <div>
                                                <Checkbox
                                                    label={`I would like to enroll in the Leadership program. ${leadershipMinDonationAmount
                                                            ? `Minimum Donation is 
                              ${getFormattedCurrency(
                                                                leadershipMinDonationAmount
                                                            )}.`
                                                            : ""
                                                        }`}
                                                    name="leadershipEnrollment"
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setFieldValue("stepUpEnrollment", false);
                                                            values?.contributionOptionId &&
                                                                checkMinimumContribution(
                                                                    values.contributionAmount ?? 0,
                                                                    { leadershipEnrollment: true }
                                                                );
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                                <Form>
                                    <ContributionSection
                                        values={{
                                            ...values,
                                            giftInvestmentAmount:
                                                values.giftInvestmentAmount ?? [],
                                        }}
                                        contributionOptions={contributionOptions}
                                        pledgeStyles={pledgeStyles}
                                        handleContributionOptionChange={handleContributionOptionChange}
                                        setFieldValue={setFieldValue}
                                        setFieldTouched={setFieldTouched}
                                        setOtherGiftOption={setOtherGiftOption}
                                        handleGiftInvestmentChange={handleGiftInvestmentChange}
                                        checkMinimumContribution={checkMinimumContribution}
                                        enableOtherAnnualPledgeAmount={enableOtherAnnualPledgeAmount}
                                    />

                                    <PaymentMethodSection
                                        show={canDisplayMethodOfGiving}
                                        values={values}
                                        paymentOptions={paymentOptions}
                                        pledgeStyles={pledgeStyles}
                                        paymentCreditCardEnabled={
                                            props.pledgeQuery?.paymentCreditCardEnabled ?? false
                                        }
                                        paymentCheckEnabled={
                                            props.pledgeQuery?.paymentCheckEnabled ?? false
                                        }
                                        paymentPayrollDeductionEnabled={
                                            props.pledgeQuery?.paymentPayrollDeductionEnabled ?? false
                                        }
                                    />

                                    <GiftInvestmentSection
                                        show={!values.isNotWillingToContribute}
                                        width={width}
                                        pledgeStyles={pledgeStyles}
                                        filteredGiftInvestmentOptions={filteredGiftInvestmentOptions}
                                        handleGiftInvestmentChange={handleGiftInvestmentChange}
                                        setFieldValue={setFieldValue}
                                        values={{
                                            ...values,
                                            giftInvestmentAmount:
                                                values.giftInvestmentAmount ?? [],
                                        }}
                                        openEditDialog={openEditDialog}
                                        undesignatedLabel={undesignatedLabel}
                                        sortedAgency={sortedAgency}
                                        employee={props.employee}
                                        dropDownArrayLength={dropDownArrayLength ?? 0}
                                        isEditModalOpen={isEditModalOpen}
                                        closeEditDialog={closeEditDialog}
                                        unEditActionHandler={unEditActionHandler}
                                        recalculateRemainder={recalculateRemainder}
                                    />
                                    <SpouseSection
                                        show={canDisplayCombineContribution}
                                        showDetails={showSpouseOtherOptions}
                                        setShowDetails={setShowSpouseOtherOptions}
                                        setFieldValue={setFieldValue}
                                        styles={pledgeStyles}
                                    />

                                    <PrivacySection
                                        show={canDisplayPrivacy}
                                        values={values}
                                        styles={pledgeStyles}
                                    />

                                    <PledgeActions
                                        values={values}
                                        styles={pledgeStyles}
                                        isValid={isValid}
                                        dirty={dirty}
                                        isDesktop={isDesktop}
                                        status={props.status}
                                        displayText={displayBottom}
                                        saveInput={saveInput}
                                        handleOnSubmit={(actionValues) =>
                                            props.handleOnSubmit(
                                                Object.assign(
                                                    new EmployeePledgeViewModel(),
                                                    values,
                                                    actionValues
                                                )
                                            )
                                        }
                                    />
                                </Form>
                            </div>
                        );
                    }}
                </Formik>
            </FormBackGround>
            <ConfirmationDialog
                closeText="No"
                confirmText="Yes"
                open={minimumContributionMessage !== null}
                onClose={() => {
                    formRef.current?.setFieldValue("stepUpEnrollment", false);
                    formRef.current?.setFieldValue("leadershipEnrollment", false);
                    setMinimumContributionMessage(null);
                }}
                onConfirm={() => {
                    setMinimumContributionMessage(null);
                    formRef.current?.setFieldValue("contributionOptionId", null);
                }}
                contentTitle={"Minimum Donation"}
                contentText={minimumContributionMessage ?? ""}
            ></ConfirmationDialog>
        </>
    );
};

export default EmployeePledgeForm;
